import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { createProxyUrl } from '@ocean/api'
import { clientConfigurationProvider as getConfigValue } from '@ocean/configs'
import { common } from '@ocean/ui'
import { UserUtils, EnvironmentUtils } from 'utils'
import { NotificationsNumbers } from 'core/environment/types'
import { LegacyMenuItem, StaRedirectParams } from 'core/staIntegration/types'
import { PromoInfo } from 'components/common'
import { User } from 'components/general'
import { SectionsNav, NavTree } from 'components/navigation'
import { AccountTypes } from 'modules/Auth/types'
import homeRoutes from 'modules/Home/routing/routes'
import {
  Overlay,
  Root,
  Wrapper,
  LogoSection,
  LogoWrapper,
  Logo,
  OceanLogo,
  LogoSmall,
  UserSection,
  SectionTitle,
  NavigationSection,
  EllipsisSection,
  NavTreeSection,
  PromoWrapper,
  VersionWrapper,
} from './Sidebar.styled'

const { Typography, Icon, Spacer, Scrollbars } = common

type SidebarProps = {
  onLogout: Callback
  onRedirect: (redirectParams: StaRedirectParams) => void
  userName?: string
  legacyMenuItems?: LegacyMenuItem[]
  notificationsCounters?: NotificationsNumbers
  logoTimestamp?: Nullable<string>
}

const Sidebar: React.FC<SidebarProps> = props => {
  const { t } = useTranslation()
  const {
    onLogout,
    userName = '',
    legacyMenuItems,
    notificationsCounters,
    logoTimestamp,
    onRedirect,
  } = props
  const appVersion = getConfigValue('appVersion')
  const { isOceanLogoOnMenuEnabled: enabledCompanyLogo } =
    EnvironmentUtils.getPermissions('general')

  const { type } = UserUtils.getUserAccountInfo()
  const isSpecialUser = type === AccountTypes.SpecialUser

  function renderLogo() {
    if (!enabledCompanyLogo) return <OceanLogo data-test='SidebarOceanLogoPic' />

    const { customer } = UserUtils.getUserLoginParams()
    const timestamp = logoTimestamp ? new Date(logoTimestamp).getTime() : ''
    const logoUrl = createProxyUrl(
      `system/customerLogo?customer=${customer}&timestamp=${timestamp}`
    )

    return (
      <LogoWrapper>
        <Logo src={logoUrl} data-test='SidebarLogoPic' />
      </LogoWrapper>
    )
  }

  function renderMoreSectionHeader() {
    if (isSpecialUser) return null
    return (
      <SectionTitle>
        <Typography variant='h4' color='cornflower' uppercase dataTest='SidebarLegacyMenuMoreLabel'>
          {t('navigation.more')}
        </Typography>
      </SectionTitle>
    )
  }

  function renderLegacyMenu() {
    if (!legacyMenuItems) return null
    return (
      <>
        <EllipsisSection data-test='SidebarLegacyMenuIcon'>
          <Icon icon='menu' />
        </EllipsisSection>
        {renderMoreSectionHeader()}
        <NavTreeSection data-test='SidebarLegacyMenuSection'>
          <NavTree
            items={legacyMenuItems}
            counters={notificationsCounters}
            onRedirect={onRedirect}
          />
        </NavTreeSection>
      </>
    )
  }

  return (
    <>
      <Root data-test='Sidebar'>
        <Scrollbars autoHeight={false}>
          <Wrapper>
            <LogoSection>
              <Link to={homeRoutes.root} data-test='SidebarLogoLink'>
                {renderLogo()}
              </Link>
              <LogoSmall data-test='SidebarLogoSmall' />
            </LogoSection>
            <UserSection>
              <User name={userName} onLogout={onLogout} />
            </UserSection>
            <NavigationSection>
              <SectionsNav onRedirect={onRedirect} />
            </NavigationSection>
            {!isSpecialUser && <Spacer size='huge' />}
            {renderLegacyMenu()}
            <PromoWrapper>
              <PromoInfo />
              <VersionWrapper>
                <Typography variant='small' color='cornflower' dataTest='SidebarAppVersion'>
                  {t('navigation.version', { version: appVersion })}
                </Typography>
              </VersionWrapper>
            </PromoWrapper>
          </Wrapper>
        </Scrollbars>
      </Root>
      <Overlay />
    </>
  )
}

export default Sidebar
